/**
 * PDF-6.7 — interactive Window-pick controller for the PLOT/PRINT dialog.
 *
 * AutoCAD parity: in the Plot dialog, plot-area = Window exposes a
 * "Window<" button. Clicking it temporarily hides the dialog and lets the
 * user rubber-band a rectangle on the canvas (two clicks: first corner →
 * second corner) to define the plot window. The dialog returns with the
 * window rect set.
 *
 * This is a DEDICATED, lightweight pick controller — NOT the drafting
 * 2-corner FSM (`scene/drafting/*`), which is coupled to the command
 * engine, draft-session state, and the live-preview entity pipeline, and
 * is overkill for a transient page-setup gesture. We mirror the proven
 * structure of the zoom-window pick (`stageInteractions.ts` /
 * `cameraApiAdapter.ts`) but feed the PRINT preview overlay instead of the
 * camera zoom, and persist the window onto the active layout's
 * `plotSettings` (PDF-6.6).
 *
 * # Reuse
 *
 *   - **Screen→world unprojection**: `clientToWorld(clientX, clientY,
 *     canvas, camera)` from `stageInteractions.ts` — the exact same
 *     `Vector3.unproject(camera)` path the wheel-zoom / drag-rubber / OSNAP
 *     handlers use. For the ACTIVE paper-space layout the live
 *     OrthographicCamera IS the paper camera, so this yields paper-mm world
 *     coords directly (no extra transform).
 *   - **Preview overlay**: the controller reports the live rectangle (world
 *     coords) via `onPreview`; the host pipes it into the existing
 *     `printPreviewWorldBounds` → `applyPrintPreview` overlay so the user
 *     sees the same translucent yellow rect that the print preview paints.
 *
 * # Gesture FSM (mirrors zoom-window)
 *
 *   1. pointerdown (LMB) → capture corner 1 (world).
 *   2. pointermove → live preview rect (corner1 → cursor world).
 *   3. pointerup (LMB, click — moved ≤ threshold) on the SECOND down, OR a
 *      second LMB-down after corner 1 is set → capture corner 2 → compute
 *      min/max rect → `onPicked(rect)`.
 *   4. Esc (or right-click / `cancel()`) → `onCancel`, rect unchanged.
 *
 * We use capture-phase listeners and `stopPropagation()` so the gesture
 * does NOT also trigger the normal selection-window rubber band or a stray
 * entity pick while the pick mode is active.
 */

import * as THREE from "three";

import { clientToWorld } from "../stageInteractions";

/** A window rectangle in world (paper-mm) coordinates. */
export interface WindowPickRect {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

/**
 * Compute the axis-aligned window rect (min/max corners) from two picked
 * world points. Order-independent — the user may pick corners in any
 * diagonal direction, exactly like AutoCAD's Window pick. Pure; unit-tested.
 */
export function cornersToWindowRect(
  a: { x: number; y: number },
  b: { x: number; y: number },
): WindowPickRect {
  return {
    minX: Math.min(a.x, b.x),
    minY: Math.min(a.y, b.y),
    maxX: Math.max(a.x, b.x),
    maxY: Math.max(a.y, b.y),
  };
}

/**
 * A picked window rect is "usable" only when it has a non-degenerate area.
 * A zero-width or zero-height pick (e.g. a double-click in place) cannot
 * define a plot window — AutoCAD ignores it and keeps waiting / cancels.
 */
export function isUsableWindowRect(rect: WindowPickRect): boolean {
  return rect.maxX - rect.minX > 1e-9 && rect.maxY - rect.minY > 1e-9;
}

export interface WindowPickBindings {
  /** The drawing-stage shell element (carries the <canvas> child). */
  readonly overlayShell: HTMLElement;
  /** Live THREE camera (read at event-time; may be null until R3F mounts). */
  readonly cameraRef: { readonly current: THREE.Camera | null };
  /**
   * Live preview callback — fired on every move with the in-progress rect
   * (corner1 → cursor) so the host can paint the overlay. Fired with `null`
   * before the first corner is set (overlay falls back to the auto preview).
   */
  readonly onPreview: (rect: WindowPickRect | null) => void;
  /** Two corners picked → final window rect (min/max). Ends the gesture. */
  readonly onPicked: (rect: WindowPickRect) => void;
  /** Esc / right-click / external cancel → abort, rect unchanged. */
  readonly onCancel: () => void;
}

/** Drag-threshold (CSS px) below which a down→up is a click, not a drag. */
const PICK_CLICK_THRESHOLD_PX = 4;

/**
 * Install the window-pick listeners. Returns a teardown that removes every
 * listener. Framework-agnostic (no React) — the host calls it from a
 * `useEffect` gated on the pick-active flag.
 *
 * AutoCAD Window-pick accepts BOTH interaction idioms (matching the
 * zoom-window pick): two discrete clicks, OR a press-drag-release. We
 * support both via the same corner state machine.
 */
export function installWindowPickController(
  bindings: WindowPickBindings,
): () => void {
  const { overlayShell, cameraRef, onPreview, onPicked, onCancel } = bindings;
  const canvas = overlayShell.querySelector(
    "canvas",
  ) as HTMLCanvasElement | null;
  // Listen on the shell (not the canvas) so the gesture works even if the
  // pointer drifts onto a sibling overlay layer; project against the canvas
  // rect (the WebGL viewport) for correct NDC.
  const target: HTMLElement = overlayShell;

  // First corner in WORLD coords (null until the first LMB-down).
  let corner1: { x: number; y: number } | null = null;
  // First-corner DOWN client coords — used to distinguish click vs drag on
  // the matching pointerup.
  let downClient: { x: number; y: number } | null = null;

  function getOrtho(): THREE.OrthographicCamera | null {
    const cam = cameraRef.current as THREE.OrthographicCamera | null;
    return cam && cam.isOrthographicCamera ? cam : null;
  }

  function projectClient(
    clientX: number,
    clientY: number,
  ): { x: number; y: number } | null {
    const ortho = getOrtho();
    if (!ortho || !canvas) return null;
    return clientToWorld(clientX, clientY, canvas, ortho);
  }

  function finish(secondWorld: { x: number; y: number }): void {
    if (!corner1) return;
    const rect = cornersToWindowRect(corner1, secondWorld);
    corner1 = null;
    downClient = null;
    onPreview(null);
    // Guard against zero-area picks the exporter would treat as blank.
    if (!isUsableWindowRect(rect)) {
      onCancel();
      return;
    }
    onPicked(rect);
  }

  function onPointerDown(e: PointerEvent): void {
    // Left button only; ignore touch (the gesture is a desktop pick — touch
    // users keep the existing pan/zoom). Right button cancels.
    if (e.pointerType === "touch") return;
    if (e.button === 2) {
      // Right-click acts as cancel (AutoCAD: Esc/right-click aborts a pick).
      e.preventDefault();
      e.stopPropagation();
      reset();
      onCancel();
      return;
    }
    if (e.button !== 0) return;
    const world = projectClient(e.clientX, e.clientY);
    if (!world) return;
    e.preventDefault();
    // Suppress the normal selection / drafting handlers while picking.
    e.stopPropagation();
    if (!corner1) {
      corner1 = world;
      downClient = { x: e.clientX, y: e.clientY };
      onPreview(cornersToWindowRect(world, world));
    } else {
      // Second discrete down → treat as the second corner immediately
      // (two-click idiom). The matching up is consumed by onPointerUp.
      finish(world);
    }
  }

  function onPointerMove(e: PointerEvent): void {
    if (e.pointerType === "touch") return;
    if (!corner1) return;
    const world = projectClient(e.clientX, e.clientY);
    if (!world) return;
    onPreview(cornersToWindowRect(corner1, world));
  }

  function onPointerUp(e: PointerEvent): void {
    if (e.pointerType === "touch") return;
    if (e.button !== 0) return;
    if (!corner1 || !downClient) return;
    const movedPx = Math.max(
      Math.abs(e.clientX - downClient.x),
      Math.abs(e.clientY - downClient.y),
    );
    e.preventDefault();
    e.stopPropagation();
    // Press-drag-release idiom: if the pointer moved past the click
    // threshold between the first down and this up, the release IS the
    // second corner. A pure click (moved ≤ threshold) leaves corner1 armed
    // so the user can click a second point separately (two-click idiom).
    if (movedPx > PICK_CLICK_THRESHOLD_PX) {
      const world = projectClient(e.clientX, e.clientY);
      if (world) finish(world);
    }
  }

  function onContextMenu(e: MouseEvent): void {
    // Right-click → cancel; never show the browser/context menu mid-pick.
    e.preventDefault();
    e.stopPropagation();
    reset();
    onCancel();
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (e.key !== "Escape") return;
    e.preventDefault();
    // Scoped cancel — sibling Escape listeners (e.g. viewportContextController's
    // window-Escape exit) must not also fire and kick the user out of MSPACE_VIA_VP.
    e.stopPropagation();
    e.stopImmediatePropagation();
    reset();
    onCancel();
  }

  function reset(): void {
    corner1 = null;
    downClient = null;
    onPreview(null);
  }

  // Capture phase so we run BEFORE the bubble-phase selection / drafting
  // handlers installed on the canvas by `installStageInteractions`.
  target.addEventListener("pointerdown", onPointerDown, true);
  target.addEventListener("pointermove", onPointerMove, true);
  target.addEventListener("pointerup", onPointerUp, true);
  target.addEventListener("contextmenu", onContextMenu, true);
  window.addEventListener("keydown", onKeyDown, true);

  return () => {
    target.removeEventListener("pointerdown", onPointerDown, true);
    target.removeEventListener("pointermove", onPointerMove, true);
    target.removeEventListener("pointerup", onPointerUp, true);
    target.removeEventListener("contextmenu", onContextMenu, true);
    window.removeEventListener("keydown", onKeyDown, true);
    // Drop any half-formed preview when torn down (mode toggled off).
    onPreview(null);
  };
}
